import { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { FormikProps } from 'formik';
import FieldText from '../../../components/general/fields/fieldText';
import { ISelect, ParamEnum } from '../../../../shared/types/shared';
import { asyncGetParams } from '../../../../services/param.service';
import { formatParams } from '../../../../shared/utils/params';
import { getOptionLabel } from '../utils/formatOrbitSelect';
import { OrbitSectionHeader } from '../components/orbitSectionHeader';
import { OrbitFieldSelect } from '../components/orbitFieldSelect';
import { filterDcfAdmissionParams, OrbitChannelFormValues } from './formData';

type Props = {
    formik: FormikProps<OrbitChannelFormValues>;
    readOnly?: boolean;
    section: 'classification' | 'correspondence';
};

export const OrbitDefensorFields = ({ formik, readOnly, section }: Props) => {
    const disabled = Boolean(readOnly);
    const [admissionOptions, setAdmissionOptions] = useState<ISelect[]>([]);
    const [loadingAdmissions, setLoadingAdmissions] = useState(false);
    const [admissionsError, setAdmissionsError] = useState('');
    const admissionLabel =
        getOptionLabel(admissionOptions, formik.values.sfcAdmission) ||
        formik.values.sfcAdmission;

    useEffect(() => {
        if (section !== 'classification') return;

        let cancelled = false;
        setLoadingAdmissions(true);
        setAdmissionsError('');

        asyncGetParams(ParamEnum.PQR_ADMISSION)
            .then((items) => {
                if (cancelled) return;
                const options = formatParams(filterDcfAdmissionParams(items));
                setAdmissionOptions(options);
                if (!options.length) {
                    setAdmissionsError('Catálogo no disponible');
                }
            })
            .catch(() => {
                if (!cancelled) setAdmissionsError('Catálogo no disponible');
            })
            .finally(() => {
                if (!cancelled) setLoadingAdmissions(false);
            });

        return () => {
            cancelled = true;
        };
    }, [section]);

    if (section === 'correspondence') {
        return (
            <>
                <Grid item xs={12}>
                    <OrbitSectionHeader title="Radicado de correspondencia" />
                </Grid>
                <Grid item xs={12}>
                    <FieldText
                        label="Radicado de Correspondencia"
                        name="correspondenceFiling"
                        size="small"
                        required
                        disabled={disabled}
                        inputProps={{ readOnly: disabled, maxLength: 200 }}
                        onBlur={formik.handleBlur}
                        touched={formik.touched.correspondenceFiling}
                        errors={formik.errors.correspondenceFiling}
                        value={formik.values.correspondenceFiling}
                        onChange={formik.handleChange}
                    />
                </Grid>
            </>
        );
    }

    return (
        <>
            <Grid item xs={12}>
                <OrbitSectionHeader title="Clasificación" />
            </Grid>
            <Grid item xs={12}>
                {readOnly ? (
                    <FieldText
                        label="Admisión (SFC)"
                        name="sfcAdmission"
                        size="small"
                        disabled
                        value={admissionLabel}
                    />
                ) : (
                    <OrbitFieldSelect
                        data={admissionOptions}
                        label="Admisión (SFC)"
                        name="sfcAdmission"
                        size="small"
                        required
                        disabled={
                            loadingAdmissions ||
                            Boolean(admissionsError) ||
                            admissionOptions.length === 0
                        }
                        emptyOptionLabel={
                            loadingAdmissions
                                ? 'Cargando admisiones...'
                                : admissionsError
                                ? 'Catálogo no disponible'
                                : 'Seleccionar...'
                        }
                        onBlur={formik.handleBlur}
                        touched={
                            formik.touched.sfcAdmission ||
                            Boolean(admissionsError)
                        }
                        errors={formik.errors.sfcAdmission || admissionsError}
                        value={formik.values.sfcAdmission}
                        onChange={formik.handleChange}
                    />
                )}
            </Grid>
        </>
    );
};
